/**
 * The `/invites` list pane. Reads `/invite/list` through the generated hook
 * and draws the loading / failed / empty / ready states. The route owns the
 * URL state (`?inviteId=`); this component takes plain props and never
 * navigates itself.
 *
 * Owns the rejected-row overlay: `useInviteDecline`'s generated `onSuccess`
 * invalidates this pending-only query about 150ms after the mutation
 * settles, and the refetched list no longer contains the declined invite.
 * R4 requires the card to stay on screen until the fan navigates away, so a
 * declined row is kept in a component-local map that dies on unmount (which
 * is S7), and `resolveInvitesListState` merges it over whatever the server
 * most recently returned.
 *
 * No `refetchInterval` here: the shell already polls this same query key
 * every 30s for the nav badge, and TanStack dedupes by key, so a second
 * interval would double the traffic.
 */
import { useInviteList } from "@sportsmet/pool-sdk/private";
import { InlineBanner } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import { SectionEmptyState } from "../../shell/SectionScreen";
import { InviteFriendsCard } from "./InviteFriendsCard";
import { InviteCard, InviteCardSkeleton } from "./InviteCard";
import { INVITE_LIST_FAILED_MESSAGE } from "./inviteErrorCopy";
import {
  resolveInvitesListState,
  type typeInviteRow,
} from "./invitesListState";
import { useInviteRejectFlow } from "./useInviteRejectFlow";

export type typeInvitesListPaneProps = {
  /** Called with the invite id when the fan wants the preview. The route navigates. */
  onOpenInvite: (inviteId: string) => void;
};

export const InvitesListPane = (props: typeInvitesListPaneProps): ReactNode => {
  const [isBannerDismissed, setIsBannerDismissed] = useState(false);

  const invites = useInviteList({});

  const items: readonly typeInviteRow[] | null =
    invites.data?.data?.invites ?? null;
  const { rejected, rejectingId, handleReject } = useInviteRejectFlow(items);

  const state = resolveInvitesListState({
    isLoading: invites.isLoading,
    hasFailed: invites.isError || Boolean(invites.data?.error),
    items,
    rejected,
  });

  return (
    <div className="flex flex-col gap-4">
      {state.phase === "loading" ? (
        <div
          className="mx-auto flex w-full max-w-180 flex-col gap-3"
          data-testid="invites-loading"
        >
          <InviteCardSkeleton />
          <InviteCardSkeleton />
          <InviteCardSkeleton />
        </div>
      ) : null}

      {state.phase === "failed" ? (
        <div data-testid="invites-failed">
          {!isBannerDismissed ? (
            <InlineBanner
              variant="warning"
              message={INVITE_LIST_FAILED_MESSAGE}
              action={{
                label: "Try again",
                onAction: () => {
                  setIsBannerDismissed(false);
                  void invites.refetch();
                },
              }}
              onDismiss={() => {
                setIsBannerDismissed(true);
              }}
            />
          ) : null}
        </div>
      ) : null}

      {state.phase === "empty" ? (
        <div data-testid="invites-empty">
          <SectionEmptyState
            headline="Nothing waiting"
            body="When someone invites you to a pool, it shows up here."
          />
        </div>
      ) : null}

      {state.phase === "ready" ? (
        <div
          className="mx-auto flex w-full max-w-180 flex-col gap-3"
          data-testid="invites-stack"
        >
          {state.cards.map((card) => (
            <InviteCard
              key={card.inviteId}
              model={card}
              isRejecting={rejectingId === card.inviteId}
              onJoin={props.onOpenInvite}
              onReject={(inviteId) => {
                void handleReject(inviteId);
              }}
            />
          ))}
        </div>
      ) : null}

      {/* ISSUE-1349: v1's Refer a friend, below whatever is waiting. */}
      {state.phase === "loading" ? null : (
        <div className="mx-auto w-full max-w-180">
          <InviteFriendsCard />
        </div>
      )}
    </div>
  );
};
